import { getVocabularyBookListApi } from "@/api/services/admin/learning-content";
import { TGetVocabularyListItemDto } from "@/api/services/admin/learning-content/dtos/get-vocabulary-list.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { DEFAULT_PER_PAGE } from "@/constants/pagination";
import { TWordData } from "@/features/learning-content/types/learningContent";
import { mapExplanationsToLanguages } from "@/features/learning-content/utils/map-explanations-to-languages";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

// Convert API vocabulary item to frontend TWordData format
const mapVocabularyItem = (item: TGetVocabularyListItemDto): TWordData => ({
  id: Number(item.id),
  wordId: item.sheetRowId != null ? String(item.sheetRowId) : "",
  wordName: item.word,
  reading: item.furigana ?? "",
  displayOrder: item.displayOrder,
  registeredDate: item.addedDate ?? "",
  updatedDate: item.lastModifiedDate ?? "",
  supportedLanguages: mapExplanationsToLanguages(item.explanations),
  category: item.category,
  imageUrl: item.imageFile ?? undefined,
});

/**
 * Custom hook for fetching paginated vocabulary book list by industry ID
 */
export const useVocabularyBookList = (industryId: string | undefined) => {
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_VOCABULARY_BOOK, "list", industryId, page],
    queryFn: () => getVocabularyBookListApi(industryId!, { page, perPage: DEFAULT_PER_PAGE }),
    enabled: !!industryId,
  });

  const response = query.data;
  const pagination = response?.pagination;
  const items = response?.data ?? [];

  const data = useMemo(() => {
    return items.map(mapVocabularyItem);
  }, [items]);

  // Invalidate all pages after sync/import
  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_VOCABULARY_BOOK, "list", industryId] });
  };

  return {
    data,
    categoryCount: response?.categoryCount,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    page,
    totalPage: pagination?.totalPage ?? 1,
    totalCount: pagination?.total ?? items.length,
    onPageChange: setPage,
    refetch,
  };
};
